<template>
  <div class="lo-site-page">
    <section class="selection-section">
      <div class="selection-header">
        <div class="eyebrow">LO WEBSITE</div>

        <h1>Choose Your Website Style</h1>

        <p>
          Preview each available design using your Loan Officer information.
          <!--Select a style below to see the live website preview.-->
        </p>
      </div>

      <div v-if="!lar" class="error-message">
        We could not determine your LAR from your user profile.
      </div>

      <div v-else-if="isCheckingLoPage" class="lo-page-state">
        <div class="lo-page-state-title">Checking Website Availability</div>
        <div class="lo-page-state-message">
          We are verifying that an active Loan Officer website is available for your profile.
        </div>
      </div>

      <div v-else-if="loPageCheckFailed" class="lo-page-state lo-page-state-error">
        <div class="lo-page-state-title">Unable to Verify Website Availability</div>
        <div class="lo-page-state-message">
          We could not verify your Loan Officer website at this time. Please try again later.
        </div>
      </div>

      <div v-else-if="hasLoPage === false" class="lo-page-state lo-page-state-warning">
        <div class="lo-page-state-title">LO Website Not Available</div>
        <div class="lo-page-state-message">
          We could not find an active Loan Officer website associated with your profile.
          Website style selection is unavailable until an LO page has been created for your LAR.
        </div>
      </div>

      <template v-else-if="hasLoPage === true">
        <div class="template-selector">
          <button v-for="template in templates"
                  :key="template.id"
                  type="button"
                  class="template-option"
                  :class="{
                            active: previewTemplate?.id === template.id,
                            saved: savedTemplate?.id === template.id
                        }"
                  @click="selectPreview(template)">
            <div class="template-number">
              {{ String(template.concept).padStart(2, '0') }}
            </div>

            <div class="template-option-content">
              <div class="template-name">
                {{ template.name }}
              </div>

              <div class="template-description">
                {{ template.shortDescription }}
              </div>
            </div>

            <div v-if="savedTemplate?.id === template.id"
                 class="current-badge">
              Current
            </div>
          </button>
        </div>

        <div v-if="previewTemplate" class="preview-toolbar">
          <div class="preview-selection">
            <div class="preview-label">
              Previewing
            </div>

            <div class="preview-name">
              {{ previewTemplate.name }}
            </div>

            <div class="preview-url">
              {{ selectedUrl }}
            </div>
          </div>

          <div class="preview-actions">
            <button v-if="savedTemplate?.id !== previewTemplate.id"
                    type="button"
                    class="btn btn-primary"
                    :disabled="isSaving"
                    @click="saveSelection">
              {{ isSaving ? 'Saving...' : 'Use This Website' }}
            </button>

            <div v-else
                 class="active-site-indicator">
              Current Website
            </div>
          </div>
        </div>
      </template>
    </section>

    <section v-if="hasLoPage === true && lar && previewTemplate && previewUrl"
             class="preview-section">
      <div class="browser-frame">
        <div class="browser-toolbar">
          <div class="browser-controls">
            <span></span>
            <span></span>
            <span></span>
          </div>

          <div class="browser-address">
            <span class="lock-icon">
              &#128274;
            </span>

            <span>
              {{ selectedUrl }}
            </span>
          </div>

          <button type="button"
                  class="refresh-button"
                  title="Reload preview"
                  @click="reloadPreview">
            &#8635;
          </button>
        </div>

        <div class="browser-content">
          <iframe :key="iframeKey"
                  :src="previewUrl"
                  :title="`${previewTemplate.name} Website Preview`"
                  class="preview-iframe"
                  @load="handlePreviewLoaded"></iframe>
        </div>
      </div>
    </section>

    <transition name="dialogfade">
      <Dialog v-if="showErrorDialog"
              :title="errorTitle"
              class="lo-site-error-dialog"
              @close="closeErrorDialog">
        <p class="error-dialog-message">
          {{ errorMsg }}
        </p>
      </Dialog>
    </transition>

    <NotificationGroup class="lo-site-notification-group">
      <Fade :appear="successNotification">
        <Notification v-if="successNotification"
                      :type="{ style: 'success', icon: true }"
                      :closable="true"
                      @close="closeSuccessNotification">
          <span>{{ notificationMsg }}</span>
        </Notification>
      </Fade>
    </NotificationGroup>
  </div>
</template>

<script setup>
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
  import { Dialog } from '@progress/kendo-vue-dialogs'
  import { Notification, NotificationGroup } from '@progress/kendo-vue-notification'
  import { Fade } from '@progress/kendo-vue-animation'
  import apiService from '../api/api.service.js'
  import auth from '../authConfig.js'
  // The URL persisted to LoSiteRedirect must always point to the public Gold Star site,
  // regardless of whether SwiftProcessingSPA is running locally, in Development, or Production.
  const redirectBaseUrl = 'https://goldstarfinancial.com/lo-sites'
  const defaultLoSiteBaseUrl = 'https://lo-sites.goldstarfinancial.com/'

  // Local development can still use the locally running CorpSiteV2 instance for the iframe preview.
  // Non-local builds preview the public site.  http://localhost:3000/lo-sites
  const previewBaseUrl =
    process.env.VUE_APP_LO_SITE_PREVIEW_URL ||
    'https://goldstarfinancial.com/lo-sites'

  const lar = ref('')
  const azureId = ref('')

  const hasLoPage = ref(null)
  const isCheckingLoPage = ref(false)
  const loPageCheckFailed = ref(false)

  const previewTemplate = ref(null)
  const savedTemplate = ref(null)

  const isSaving = ref(false)
  const iframeKey = ref(0)

  const successNotification = ref(false)
  const notificationMsg = ref('')
  const showErrorDialog = ref(false)
  const errorTitle = ref('')
  const errorMsg = ref('')

  let notificationTimer = null

  const templates = [
    {
      id: 0,
      concept: 0,
      name: 'Default LO Site',
      isDefault: true,
      shortDescription: 'Original Gold Star Loan Officer website'
    },
    {
      id: 1,
      concept: 1,
      name: 'Editorial',
      slug: 'editorial',
      shortDescription: 'Polished corporate layout'
    },
    {
      id: 2,
      concept: 2,
      name: 'Simple',
      slug: 'simple',
      shortDescription: 'Clean single-page experience'
    },
    {
      id: 3,
      concept: 3,
      name: 'Portrait Split',
      slug: 'portrait',
      shortDescription: 'Personal, profile-focused design'
    },
    {
      id: 4,
      concept: 4,
      name: 'Warm Hospitality',
      slug: 'hospitality',
      shortDescription: 'Welcoming and relationship driven'
    },
    {
      id: 5,
      concept: 5,
      name: 'Modern Speed',
      slug: 'speed',
      shortDescription: 'Fast, modern and energetic'
    },
    {
      id: 6,
      concept: 6,
      name: 'Division Brand',
      slug: 'brand',
      shortDescription: 'Styled around division branding'
    }
  ]

  const selectedUrl = computed(() => {
    return buildLoSiteUrl(previewTemplate.value, redirectBaseUrl)
  })

  const previewUrl = computed(() => {
    const url = buildLoSiteUrl(previewTemplate.value, previewBaseUrl)

    if (!url) {
      return ''
    }

    if (previewTemplate.value?.isDefault) {
      return url
    }

    return `${url}?embeddedPreview=true`
  })

  onMounted(async () => {
    loadCurrentUser()

    if (!lar.value || !azureId.value) {
      return
    }

    await loadLoPageStatus()

    if (hasLoPage.value !== true) {
      return
    }

    await loadCurrentSelection()
  })

  onBeforeUnmount(() => {
    if (notificationTimer) {
      clearTimeout(notificationTimer)
    }
  })

  function loadCurrentUser() {
    try {
      const userAzureData = JSON.parse(
        localStorage.getItem('user_azure_data') || '{}'
      )

      azureId.value = userAzureData?.id ?? ''

      lar.value =
        userAzureData
          ?.onPremisesExtensionAttributes
          ?.extensionAttribute1 ?? ''

      if (!lar.value) {
        console.error(
          'extensionAttribute1 was not found in user_azure_data.'
        )

        return
      }
    } catch (error) {
      console.error(
        'Unable to load user_azure_data from localStorage.',
        error
      )
    }
  }

  async function loadLoPageStatus() {
    isCheckingLoPage.value = true
    loPageCheckFailed.value = false
    hasLoPage.value = null

    try {
      const tokenResponse = await auth.acquireTokenResponse()

      if (!tokenResponse?.accessToken) {
        throw new Error('Unable to acquire an authentication token.')
      }

      const response = await apiService.HasLoPage(
        lar.value,
        tokenResponse.accessToken
      )

      const isSuccess = response?.data?.isSuccess ?? response?.data?.issuccess

      if (isSuccess === false) {
        throw new Error(
          response?.data?.message ||
          'Unable to determine LO website availability.'
        )
      }

      hasLoPage.value = response?.data?.data === true

      // if (!hasLoPage.value) {
      //   previewTemplate.value = null
      //   savedTemplate.value = null

      //   showRequestError(
      //     'LO Website Not Available',
      //     'We could not find an active Loan Officer website associated with your profile. Website style selection is unavailable until an LO page has been created for your LAR.'
      //   )
      // }
    } catch (error) {
      console.error(
        'Unable to determine LO website availability.',
        error
      )

      hasLoPage.value = null
      loPageCheckFailed.value = true
      previewTemplate.value = null
      savedTemplate.value = null

      showRequestError(
        'Unable to Check Website Availability',
        getErrorMessage(
          error,
          'We could not verify whether an active Loan Officer website is available for your profile. Please try again.'
        )
      )
    } finally {
      isCheckingLoPage.value = false
    }
  }

  async function loadCurrentSelection() {
    try {
      const tokenResponse = await auth.acquireTokenResponse()

      if (!tokenResponse?.accessToken) {
        throw new Error('Unable to acquire an authentication token.')
      }

      const response = await apiService.GetLoBypassURL(
        azureId.value,
        tokenResponse.accessToken
      )

      const isSuccess = response?.data?.isSuccess ?? response?.data?.issuccess

      if (isSuccess === false) {
        throw new Error(
          response?.data?.message ||
          'Unable to load your current LO website selection.'
        )
      }

      const redirect = response?.data?.data
      const redirectUrl = redirect?.redirectUrl ?? redirect?.redirecturl

      if (!redirectUrl) {
        savedTemplate.value = templates[0]
        previewTemplate.value = templates[0]

        showSuccessNotification(
          'Default LO Site is your current website.'
        )

        return
      }

      const currentTemplate = findTemplateByRedirectUrl(redirectUrl)

      if (!currentTemplate) {
        previewTemplate.value = templates[0]

        showRequestError(
          'Website Selection Error',
          'Your saved website redirect was returned successfully, but it does not match one of the available website styles.'
        )

        return
      }

      savedTemplate.value = currentTemplate
      previewTemplate.value = currentTemplate

      showSuccessNotification(
        `${currentTemplate.name} is your current website.`
      )
    } catch (error) {
      console.error(
        'Unable to load the current LO website selection.',
        error
      )

      previewTemplate.value = templates[0]

      showRequestError(
        'Unable to Load Current Website',
        getErrorMessage(
          error,
          'We could not load your current website selection. Please try again.'
        )
      )
    }
  }

  function findTemplateByRedirectUrl(redirectUrl) {
    if (!redirectUrl) {
      return templates[0]
    }

    try {
      const url = new URL(redirectUrl)
      const pathParts = url.pathname.split('/').filter(Boolean)

      if (url.hostname.toLowerCase() === 'lo-sites.goldstarfinancial.com') {
        return templates[0]
      }

      if (pathParts[0]?.toLowerCase() === 'lo-sites') {
        const slug = pathParts[1]?.toLowerCase()

        return templates.find(template =>
          template.slug?.toLowerCase() === slug
        ) ?? null
      }
    } catch (error) {
      console.warn('Unable to parse saved LO redirect URL.', error)
    }

    return null
  }

  function buildLoSiteUrl(template, baseUrl) {
    if (!template || !lar.value) {
      return ''
    }

    if (template.isDefault) {
      return `${defaultLoSiteBaseUrl}?lar=${encodeURIComponent(lar.value)}`
    }

    if (!template.slug || !baseUrl) {
      return ''
    }

    return `${baseUrl}/${template.slug}/${encodeURIComponent(lar.value)}`
  }

  function selectPreview(template) {
    previewTemplate.value = template

    console.log(
      'LO Site Preview URL:',
      template.isDefault
        ? buildLoSiteUrl(template, previewBaseUrl)
        : `${buildLoSiteUrl(template, previewBaseUrl)}?embeddedPreview=true`
    )

    console.log(
      'LO Site Redirect URL:',
      buildLoSiteUrl(template, redirectBaseUrl)
    )
  }

  function reloadPreview() {
    iframeKey.value++
  }

  function handlePreviewLoaded() {
    // console.log(
    //   'Preview iframe loaded:',
    //   previewUrl.value
    // )
  }

  async function saveSelection() {
    if (!previewTemplate.value || !selectedUrl.value || !azureId.value || !lar.value) {
      return
    }

    isSaving.value = true

    try {
      const tokenResponse = await auth.acquireTokenResponse()

      if (!tokenResponse?.accessToken) {
        throw new Error('Unable to acquire an authentication token.')
      }

      let response

      if (previewTemplate.value.isDefault) {
        response = await apiService.DeleteLoBypassURL(
          azureId.value,
          tokenResponse.accessToken
        )
      } else {
        const request = {
          azureId: azureId.value,
          lar: lar.value,
          redirectUrl: selectedUrl.value
        }

        response = await apiService.UpdateLoBypassURL(
          request,
          tokenResponse.accessToken
        )
      }

      const isSuccess = response?.data?.isSuccess ?? response?.data?.issuccess

      if (isSuccess === false) {
        throw new Error(
          response?.data?.message ||
          'Unable to save LO website selection.'
        )
      }

      savedTemplate.value = previewTemplate.value

      if (previewTemplate.value.isDefault) {
        showSuccessNotification(
          'Default LO Site restored successfully. Your custom redirect has been removed.'
        )
      } else {
        showSuccessNotification(
          `${previewTemplate.value.name} is now your current website.`
        )
      }
    } catch (error) {
      console.error(
        'Unable to save LO website selection.',
        error
      )

      showRequestError(
        previewTemplate.value?.isDefault
          ? 'Unable to Restore Default Website'
          : 'Unable to Update Website',
        getErrorMessage(
          error,
          'We could not update your website selection. Please try again.'
        )
      )
    } finally {
      isSaving.value = false
    }
  }

  function showSuccessNotification(message) {
    notificationMsg.value = message
    successNotification.value = true

    if (notificationTimer) {
      clearTimeout(notificationTimer)
    }

    notificationTimer = setTimeout(() => {
      successNotification.value = false
      notificationTimer = null
    }, 4000)
  }

  function closeSuccessNotification() {
    successNotification.value = false

    if (notificationTimer) {
      clearTimeout(notificationTimer)
      notificationTimer = null
    }
  }

  function showRequestError(title, message) {
    errorTitle.value = title
    errorMsg.value = message
    showErrorDialog.value = true
  }

  function closeErrorDialog() {
    showErrorDialog.value = false
    errorTitle.value = ''
    errorMsg.value = ''
  }

  function getErrorMessage(error, fallbackMessage) {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.data ||
      error?.message ||
      fallbackMessage
    )
  }

</script>

<style scoped>
  .lo-site-page,
  .lo-site-page * {
    box-sizing: border-box;
  }

  .lo-site-page {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    /*    min-height: 100vh;*/
    height: auto;
    background: #f5f7fa;
    overflow: auto;
  }

  .selection-section {
    flex: 0 0 auto;
    width: 100%;
    padding: 34px 40px 28px;
    color: #ffffff;
    background: linear-gradient(135deg, #071a31 0%, #102d4d 60%, #173a60 100%);
  }

  .selection-header {
    width: 100%;
    max-width: 1500px;
    margin: 0 auto 28px;
  }

  .eyebrow {
    margin-bottom: 8px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.2em;
    color: #9fb3c8;
  }

  .selection-header h1 {
    margin: 0 0 10px;
    font-size: 30px;
    font-weight: 700;
  }

  .selection-header p {
    max-width: 760px;
    margin: 0;
    line-height: 1.6;
    color: #c9d5e1;
  }

  .template-selector {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    width: 100%;
    max-width: 1500px;
    margin: 0 auto;
  }

  .template-option {
    position: relative;
    display: flex;
    min-width: 0;
    /*    min-height: 115px;*/
    padding: 16px;
    text-align: left;
    color: #dbe6f0;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.13);
    border-radius: 12px;
    cursor: pointer;
    transition: transform 0.15s ease, background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  }

    .template-option:hover {
      transform: translateY(-2px);
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .template-option.active {
      color: #102744;
      background: #ffffff;
      border-color: #ffffff;
      box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
    }

  .template-number {
    flex: 0 0 auto;
    margin-right: 12px;
    padding-top: 2px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    opacity: 0.6;
  }

  .template-option-content {
    min-width: 0;
  }

  .template-name {
    margin-bottom: 5px;
    font-size: 15px;
    font-weight: 700;
  }

  .template-description {
    font-size: 12px;
    line-height: 1.45;
    opacity: 0.72;
  }

  .current-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    padding: 3px 7px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #ffffff;
    background: #287552;
    border-radius: 999px;
  }

  .preview-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    width: 100%;
    max-width: 1500px;
    margin: 24px auto 0;
    padding-top: 22px;
    border-top: 1px solid rgba(255, 255, 255, 0.15);
  }

  .preview-selection {
    min-width: 0;
  }

  .preview-label {
    margin-bottom: 2px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #9fb3c8;
  }

  .preview-name {
    margin-bottom: 3px;
    font-size: 18px;
    font-weight: 700;
  }

  .preview-url {
    max-width: 850px;
    overflow: hidden;
    font-size: 12px;
    color: #9fb3c8;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .preview-actions {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }

  .btn {
    padding: 10px 20px;
    border: 0;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 700;
  }

    .btn:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }

  .btn-primary {
    color: #102744;
    background: #ffffff;
  }

    .btn-primary:hover:not(:disabled) {
      background: #edf2f7;
    }

  .active-site-indicator {
    padding: 10px 18px;
    font-size: 13px;
    font-weight: 700;
    color: #b9efd2;
    background: rgba(39, 117, 82, 0.25);
    border: 1px solid rgba(123, 219, 167, 0.35);
    border-radius: 8px;
  }

  .preview-section {
    flex: 0 0 auto;
    width: 100%;
    /*    max-width: 1550px;*/
    height: auto;
    margin: 0 auto;
    padding: 20px;
    overflow: visible;
  }

  .browser-frame {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    height: auto;
    overflow: hidden;
    background: #ffffff;
    border: 1px solid #d9e0e7;
    border-radius: 14px;
    box-shadow: 0 18px 50px rgba(15, 35, 55, 0.12);
  }

  .browser-toolbar {
    display: flex;
    flex: 0 0 52px;
    align-items: center;
    gap: 16px;
    width: 100%;
    height: 52px;
    padding: 0 16px;
    background: #edf1f5;
    border-bottom: 1px solid #d9e0e7;
  }

  .browser-controls {
    display: flex;
    flex: 0 0 auto;
    gap: 7px;
  }

    .browser-controls span {
      width: 11px;
      height: 11px;
      background: #b7c0ca;
      border-radius: 50%;
    }

  .browser-address {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    height: 32px;
    padding: 0 14px;
    overflow: hidden;
    font-size: 12px;
    color: #657486;
    background: #ffffff;
    border: 1px solid #d5dde5;
    border-radius: 7px;
  }

    .browser-address span:last-child {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

  .lock-icon {
    flex: 0 0 auto;
    margin-right: 7px;
    font-size: 10px;
  }

  .refresh-button {
    flex: 0 0 32px;
    width: 32px;
    height: 32px;
    padding: 0;
    color: #536476;
    background: transparent;
    border: 0;
    border-radius: 6px;
    cursor: pointer;
    font-size: 20px;
  }

    .refresh-button:hover {
      background: #dfe5eb;
    }

  .browser-content {
    position: relative;
    width: 100%;
    height: clamp(700px, 78vh, 950px);
    min-height: 700px;
    overflow: hidden;
    background: #ffffff;
  }

  .preview-iframe {
    display: block;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    background: #ffffff;
    border: 0;
  }

  .error-message {
    width: 100%;
    max-width: 1500px;
    margin: 20px auto 0;
    padding: 14px 16px;
    color: #fecaca;
    background: rgba(185, 28, 28, 0.2);
    border: 1px solid rgba(248, 113, 113, 0.35);
    border-radius: 8px;
  }

  .lo-page-state {
    width: 100%;
    max-width: 1500px;
    margin: 20px auto 0;
    padding: 22px 24px;
    color: #dbe6f0;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.13);
    border-radius: 12px;
  }

  .lo-page-state-title {
    margin-bottom: 6px;
    font-size: 16px;
    font-weight: 700;
    color: #ffffff;
  }

  .lo-page-state-message {
    max-width: 850px;
    font-size: 13px;
    line-height: 1.55;
    color: #c9d5e1;
  }

  .lo-page-state-warning {
    background: rgba(180, 120, 20, 0.16);
    border-color: rgba(245, 184, 75, 0.32);
  }

  .lo-page-state-error {
    background: rgba(185, 28, 28, 0.16);
    border-color: rgba(248, 113, 113, 0.32);
  }

  .lo-site-notification-group {
    position: fixed;
    right: 24px;
    bottom: 5px;
    z-index: 30000;
    /*    width: min(420px, calc(100vw - 32px));*/
  }

  :deep(.lo-site-notification-group .k-notification) {
    /*    width: 100%;*/
    /*    box-shadow: 0 14px 40px rgba(15, 35, 55, 0.22);*/
  }

  :deep(.lo-site-notification-group .k-notification-success) {
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: center;
    background-color: var(--primary);
    border-bottom-color: var(--primary);
  }

  :deep(.lo-site-error-dialog .k-dialog) {
    width: min(520px, calc(100vw - 32px));
    border-radius: 12px;
    overflow: hidden;
  }

  :deep(.lo-site-error-dialog .k-dialog-titlebar) {
    padding: 20px 16px;
    background-color: var(--error) !important;
    color: var(--on-error) !important;
    border-bottom: unset !important;
    display: flex;
    align-items: center;
  }

  :deep(.lo-site-error-dialog .k-dialog .k-svg-icon) {
    color: var(--background) !important;
  }

  .error-dialog-message {
    margin: 10px;
    text-align: center;
    font-weight: 700;
  }

  @media (max-width: 1250px) {
    .template-selector {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .browser-content {
      height: clamp(680px, 76vh, 850px);
      min-height: 680px;
    }
  }

  @media (max-width: 800px) {
    .selection-section {
      padding: 24px 20px;
    }

    .template-selector {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .preview-toolbar {
      align-items: stretch;
      flex-direction: column;
    }

    .preview-actions {
      align-items: stretch;
      width: 100%;
    }

      .preview-actions .btn {
        width: 100%;
      }

    .preview-section {
      padding: 20px;
    }

    .browser-toolbar {
      gap: 10px;
      padding: 0 10px;
    }

    .browser-controls {
      display: none;
    }

    .browser-content {
      height: 700px;
      min-height: 700px;
    }
  }

  @media (max-width: 520px) {
    .template-selector {
      grid-template-columns: 1fr;
    }

    .selection-header h1 {
      font-size: 26px;
    }

    .preview-url {
      white-space: normal;
      overflow-wrap: anywhere;
    }
  }
</style>
